// src/components/DashboardComponent/AdminResearch.js
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import Back from '../../assets/bisu.png';
import { useNavigate } from 'react-router-dom';
import AddResearchForm from './AddResearchForm';
import { getAllResearch, deleteResearch } from '../../utils/researchApi';
import Logo from '../../assets/Logo-RB.png';
import loadingGif from '../../assets/4.gif';

// --- Styled Components ---

const Container = styled.div`
  padding: 0 2rem 6rem;
  max-width: 1200px;
  margin: 0 auto;
  min-height: 100vh;
  color: #3B7A57;
  position: relative;
  overflow: hidden;
  padding-top: 140px;

  &::before {
    content: '';
    position: absolute;
    top: 0; 
    left: 0;
    width: 100%;
    height: 100%;
    background: url(${Back}) no-repeat center center;
    background-size: cover;
    filter: brightness(0.55) contrast(1.02);
    z-index: -2;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0; 
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(180deg, rgba(4,10,20,0.32), rgba(4,10,20,0.58));
    z-index: -1;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

const Header = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background: linear-gradient(90deg, #2d5016 0%, #3d6b1f 50%, #2d5016 100%);
  box-shadow: 0 4px 12px rgba(45, 80, 22, 0.25);
  z-index: 1001;
  margin-bottom: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const HeaderLogo = styled.img`
  height: 60px;
  width: auto;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
`;

const HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
`;

const MainTitle = styled.h1`
  font-family: 'Chau Philomene One', cursive;
  font-size: 2.4rem;
  color: #f5f6f5ff;
  margin: 0;
  letter-spacing: 1px;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: #e0e0e0;
  margin: 0.5rem 0 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const AddButton = styled.button`
  background: #f4f1e8;
  color: #2d5016;
  padding: 0.8rem 1.5rem;
  border: 2px solid #3d6b1f;
  border-radius: 30px;
  font-weight: bold;
  cursor: pointer;
  font-size: 1.1rem;
  transition: all 0.3s ease;

  &:hover {
    background: #3d6b1f;
    color: #f4f1e8;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(61, 107, 31, 0.35);
  }
`;

const LogoutButton = styled.button`
  background: transparent;
  color: #f4f1e8;
  padding: 0.8rem 1.5rem;
  border: 2px solid rgba(244, 241, 232, 0.4);
  border-radius: 30px;
  font-weight: bold;
  cursor: pointer;
  font-size: 1.1rem;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(61, 107, 31, 0.3);
    border-color: #f4f1e8;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(244, 241, 232, 0.25);
  }
`;

const Alert = styled.div`
  padding: 1rem 1.1rem;
  border-radius: 10px;
  margin-bottom: 1rem;
  border: 1px solid;
`;

const ErrorAlert = styled(Alert)`
  background: linear-gradient(180deg, rgba(255,80,80,0.06), rgba(255,80,80,0.03));
  color: #ffd4d4;
  border-color: rgba(255,80,80, 0.08);
`;

const SuccessAlert = styled(Alert)`
  background: linear-gradient(180deg, rgba(80,255,150,0.04), rgba(80,255,150,0.02));
  color: #e6ffef;
  border-color: rgba(80,255,150, 0.06);
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10rem;
  color: #cfe7ff;
  font-size: 1.1rem;
  flex-direction: column;
`;

const ResearchList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ResearchCard = styled.div`
  background: linear-gradient(135deg, #e8f5e9 0%, #f1f8f4 50%, #faf9f6 100%);
  padding: 1.5rem;
  border-radius: 16px;
  border: 2px solid #c8e6c9;
  box-shadow: 0 4px 12px rgba(45, 80, 22, 0.12);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: stretch;

  &:hover {
    transform: translateX(8px);
    border-color: #81c784;
    box-shadow: 0 8px 24px rgba(61, 107, 31, 0.2);
  }
`;

const CardContentAndActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2rem;
  width: 100%;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CardActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  justify-content: flex-start;
  align-items: stretch;
  min-width: 120px;

  @media (max-width: 768px) {
    flex-direction: row;
    min-width: auto;
    justify-content: center;
  }
`;

const CardTitle = styled.a` 
  font-family: 'Chau Philomene One', cursive;
  font-size: 1.5rem;
  color: #2d5016;
  margin: 0 0 0.5rem 0;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s ease, text-decoration 0.2s ease;

  &:hover {
    color: #1a3c0f;
    text-decoration: underline;
  }
`;

const CardMeta = styled.p`
  font-size: 0.9rem;
  color: #5a6b52;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const StatusBadge = styled.span`
  background: ${props => props.published
    ? 'linear-gradient(135deg, #3d6b1f 0%, #4a7f2a 100%)'
    : 'linear-gradient(135deg, #c4b5a0 0%, #d4c5b0 100%)'};
  color: ${props => props.published ? '#f4f1e8' : '#2d5016'};
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  box-shadow: 0 6px 18px rgba(45, 80, 22, 0.15);
`;

const CardDescription = styled.p`
  font-size: 0.98rem;
  line-height: 1.5;
  margin: 0 0 1rem 0;
  color: #000000;
  font-weight: 600;
  ${props => props.isExpanded ? '' : `
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`;

const SeeMoreButton = styled.button`
  background: none;
  border: none;
  color: #136F07;
  font-weight: 600;
  cursor: pointer;
  margin-top: 0.5rem;
  align-self: flex-start;
  padding: 0;
  font-size: 0.95rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

const CardButton = styled.button`
  border: none;
  padding: 0.45rem 0.85rem;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 700;
  color: white;
  background: linear-gradient(90deg, #3B7A57, #f4fbf84d); 
  box-shadow: 0 6px 18px rgba(9, 69, 36, 0.11);
  transition: transform 0.12s ease, box-shadow 0.12s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 24px rgba(59, 122, 87, 0.18);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EditButton = styled(CardButton)`
  background: #9fc8efff;
  color: #000000;
  border-radius: 5px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.14);
  transition: box-shadow 0.2s ease, transform 0.1s ease;

  &:active {
    box-shadow: 0 10px 24px rgba(34, 197, 94, 0.8);
    transform: scale(0.97);
  }
`;

const DeleteButton = styled(CardButton)`
  background: #f76808ff;
  color: #000000;
  border-radius: 5px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.14);
  transition: box-shadow 0.2s ease, transform 0.1s ease;

  &:active {
    box-shadow: 0 10px 24px rgba(22, 97, 49, 0.66);
    transform: scale(0.97);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  background: linear-gradient(180deg, rgba(195, 200, 202, 0.02), rgba(255,255,255,0.01));
  border-radius: 12px;
  color: #eff1f3ff;
  box-shadow: 0 14px 40px rgba(229, 231, 240, 0.6);
`;

const FilterBar = styled.div`
  background: rgba(255, 255, 255, 0.7);
  padding: 1rem;
  border-radius: 15px;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  border: 2px solid rgba(129, 199, 132, 0.2);
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterLabel = styled.span`
  font-weight: 600;
  color: #2d5016;
  font-size: 0.95rem;
`;

const FilterButton = styled.button`
  padding: 0.5rem 1rem;
  border: 2px solid ${props => props.active ? '#3d6b1f' : '#c8e6c9'};
  background: ${props => props.active ? '#3d6b1f' : 'white'};
  color: ${props => props.active ? 'white' : '#2d5016'};
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? '#2d5016' : '#f1f8f4'};
    border-color: #3d6b1f;
  }
`;

// --- Component Logic ---

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [researches, setResearches] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editResearch, setEditResearch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  // Filter states
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const toggleDescription = (id) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('currentUser');
    if (!token || !user) {
      alert('Please log in to access this page');
      localStorage.clear();
      navigate('/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      localStorage.clear();
      navigate('/');
    }
  };

  const fetchResearches = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllResearch();
      setResearches(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ Failed to load research:', err);
      setError(err.message);
      if (err.message.includes('log in') || err.message.includes('401')) {
        setTimeout(() => {
          alert('Session expired. Please log in again.');
          localStorage.clear();
          navigate('/login');
        }, 1000);
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchResearches();
  }, [fetchResearches]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this research?')) return;

    try {
      setLoading(true);
      setError(null);
      await deleteResearch(id);
      setSuccess('Research deleted successfully!');
      await fetchResearches();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Delete failed:', err);
      setError(err.message);
      if (err.message.includes('log in') || err.message.includes('401')) {
        setTimeout(() => {
          alert('Session expired. Please log in again.');
          localStorage.clear();
          navigate('/login');
        }, 1000);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFormSuccess = () => {
    setShowAddForm(false);
    setEditResearch(null);
    setSuccess('Research added/updated successfully!');
    fetchResearches();
    setTimeout(() => setSuccess(null), 3000);
  };


  // Filter researches
  const filteredResearches = researches.filter(research => {
    const statusMatch = filterStatus === 'all' ||
      (filterStatus === 'published' && research.published) ||
      (filterStatus === 'draft' && !research.published);

    const typeMatch = filterType === 'all' || research.type === filterType;

    return statusMatch && typeMatch;
  });
  return (
    <Container>
      <Header>
        <HeaderLeft>
          <HeaderLogo src={Logo} alt="M.A.K.A.S.A Logo" />
          <HeaderTitle>
            <MainTitle>M.A.K.A.S.A</MainTitle>
            <Subtitle>Admin Agricultural Research System</Subtitle>
          </HeaderTitle>
        </HeaderLeft>
        <ButtonGroup>
          <AddButton onClick={() => {
            if (showAddForm) {
              setShowAddForm(false);
              setEditResearch(null);
            } else {
              setEditResearch(null);
              setShowAddForm(true);
            }
          }}>
            {showAddForm ? '✕ Cancel' : '+ Add Research'}
          </AddButton>
          <LogoutButton onClick={handleLogout}>
            Logout
          </LogoutButton>
        </ButtonGroup>
      </Header>

      {error && <ErrorAlert>❌ <strong>Error:</strong> {error}</ErrorAlert>}
      {success && <SuccessAlert>✅ {success}</SuccessAlert>}

      {showAddForm && (
        <div style={{ marginBottom: '2rem' }}>
          <AddResearchForm onSuccess={handleFormSuccess} initialData={editResearch} />
        </div>
      )}


      {/* Filter Bar */}
      {!loading && !showAddForm && researches.length > 0 && (
        <FilterBar>
          <FilterGroup>
            <FilterLabel>Status:</FilterLabel>
            <FilterButton
              active={filterStatus === 'all'}
              onClick={() => setFilterStatus('all')}
            >
              All
            </FilterButton>
            <FilterButton
              active={filterStatus === 'published'}
              onClick={() => setFilterStatus('published')}
            >
              Published
            </FilterButton>
            <FilterButton
              active={filterStatus === 'draft'}
              onClick={() => setFilterStatus('draft')}
            >
              Drafts
            </FilterButton>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Type:</FilterLabel>
            <FilterButton
              active={filterType === 'all'}
              onClick={() => setFilterType('all')}
            >
              All Types
            </FilterButton>
            <FilterButton
              active={filterType === 'Journal'}
              onClick={() => setFilterType('Journal')}
            >
              Journals
            </FilterButton>
            <FilterButton
              active={filterType === 'Conference'}
              onClick={() => setFilterType('Conference')}
            >
              Conferences
            </FilterButton>
            <FilterButton
              active={filterType === 'Thesis'}
              onClick={() => setFilterType('Thesis')}
            >
              Thesis
            </FilterButton>
          </FilterGroup>


        </FilterBar>
      )}

      {loading && (
        <LoadingSpinner>
          <img
            src={loadingGif}
            alt="Loading..."
            style={{
              width: '100px',
              height: '100px',
              marginBottom: '1rem',
            }}
          />
        </LoadingSpinner>
      )}

      {!loading && (
        <ResearchList>
          {filteredResearches.length === 0 ? (
            <EmptyState>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📚</div>
              <h3>No research entries found</h3>
              <p>
                {researches.length === 0
                  ? 'Click "Add Research" to create your first entry.'
                  : 'No research matches.'}
              </p>
            </EmptyState>
          ) : (
            filteredResearches.map((research) => {
              const isLongDescription = research.description && research.description.length > 200;
              const isExpanded = expandedDescriptions[research.id] || false;

              return (
                <ResearchCard key={research.id}>
                  {/* Container for content and actions side-by-side */}
                  <CardContentAndActions>
                    <CardContent>
                      {/* ✅ Make the title a clickable link */}
                      <CardTitle
                        href={research.link || "#"} // Use the research link, fallback to "#" if no link
                        target="_blank" // Open in new tab
                        rel="noopener noreferrer" // Security best practice for target="_blank"
                      >
                        {research.title}
                      </CardTitle>

                      <CardMeta>
                        <span>📅 {research.date ? new Date(research.date).toLocaleDateString() : 'No date'}</span>
                        <span>• {research.type}</span>
                        <StatusBadge published={research.published}>
                          {research.published ? '✓ Published' : '✗ Draft'}
                        </StatusBadge>
                      </CardMeta>
                      {/* adjust the description if you want */}
                      <CardDescription isExpanded={isExpanded}>
                        {research.description
                          ? (research.description.length > 30
                            ? `${research.description.substring(0, 30)}...`
                            : research.description)
                          : 'No description available'}
                      </CardDescription>
                      {isLongDescription && (
                        <SeeMoreButton onClick={() => toggleDescription(research.id)}>
                        </SeeMoreButton>
                      )}
                      {research.link && (
                        <p style={{ fontSize: '0.9rem', color: '#2196F3', marginBottom: '1rem' }}>
                          🔗 <a href={research.link} target="_blank" rel="noopener noreferrer">External Link</a>
                        </p>
                      )}
                      {research.file_path && (
                        <p style={{ fontSize: '0.9rem', color: '#4CAF50', marginBottom: '1rem' }}>
                          📎 File: {research.file_path}
                        </p>
                      )}
                    </CardContent>

                    <CardActions>
                      <EditButton onClick={() => {
                        setEditResearch(research);
                        setShowAddForm(true);
                        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                      }}>
                        Edit
                      </EditButton>
                      <DeleteButton onClick={() => handleDelete(research.id)} disabled={loading}>
                        Delete
                      </DeleteButton>
                    </CardActions>
                  </CardContentAndActions>
                </ResearchCard>
              );
            })
          )}
        </ResearchList>
      )}
    </Container>
  );
};

export default AdminDashboard;